import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { AtivacaoMAC, CategoriaAtivacao, StatusAtivacao } from '@/lib/types';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/PageHeader';
import { Skeleton } from '@/components/Skeleton';
import {
  Plus, ExternalLink, Video, Sparkles, X, Check, Pencil, Trash2,
} from 'lucide-react';
import clsx from 'clsx';

const CATEGORIAS: CategoriaAtivacao[] = [
  'Real Estate', 'Áreas de Negócios', 'Produtos da Gestora',
  'Research & Iscas', 'Teses', 'Serviços', 'Institucional',
];

const CAT_COLORS: Record<CategoriaAtivacao, string> = {
  'Real Estate': 'bg-blue-50 border-blue-200 text-blue-700',
  'Áreas de Negócios': 'bg-cyan-50 border-cyan-200 text-cyan-700',
  'Produtos da Gestora': 'bg-green-50 border-green-200 text-green-700',
  'Research & Iscas': 'bg-purple-50 border-purple-200 text-purple-700',
  'Teses': 'bg-pink-50 border-pink-200 text-pink-700',
  'Serviços': 'bg-amber-50 border-amber-200 text-amber-700',
  'Institucional': 'bg-slate-50 border-slate-200 text-slate-700',
};

export default function MacEnxoval() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<CategoriaAtivacao | 'Todas'>('Todas');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<AtivacaoMAC | null>(null);

  const { data: ativacoes, isLoading } = useQuery({
    queryKey: ['mac-ativacoes'],
    queryFn: () => api.mac.ativacoes.list().then((r) => r.data),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.mac.ativacoes.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mac-ativacoes'] }),
  });

  const aprovarMut = useMutation({
    mutationFn: (id: string) => api.mac.ativacoes.aprovar(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mac-ativacoes'] }),
  });

  function handleAprovar(a: AtivacaoMAC) {
    if (!confirm(`Aprovar "${a.nome}"? Ela passa a ATIVA — sinal de que o material/LP/oferta está confirmado e pronto para os eventos.`)) return;
    aprovarMut.mutate(a.id);
  }

  const canEdit = user?.papel === 'Coordenação' || user?.papel === 'Diretoria';

  const filtered = tab === 'Todas'
    ? ativacoes ?? []
    : (ativacoes ?? []).filter((a) => a.categoria === tab);

  const countByCategoria = (cat: CategoriaAtivacao) =>
    (ativacoes ?? []).filter((a) => a.categoria === cat).length;

  function handleDelete(a: AtivacaoMAC) {
    if (!confirm(`Excluir "${a.nome}"?`)) return;
    deleteMut.mutate(a.id);
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Enxoval de Ativações"
        subtitle="Catálogo de ativações promocionais MAC"
      />

      {/* Tabs */}
      <div className="flex gap-1.5 flex-wrap">
        <TabBtn active={tab === 'Todas'} onClick={() => setTab('Todas')}>
          Todas ({ativacoes?.length ?? 0})
        </TabBtn>
        {CATEGORIAS.map((cat) => (
          <TabBtn key={cat} active={tab === cat} onClick={() => setTab(cat)}>
            {cat} ({countByCategoria(cat)})
          </TabBtn>
        ))}
      </div>

      {/* Actions */}
      {canEdit && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue text-white text-xs font-medium hover:bg-blue/90 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Nova ativação
          </button>
        </div>
      )}

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-lg" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted text-sm">Nenhuma ativação cadastrada.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((a) => (
            <AtivacaoCard key={a.id} ativacao={a} canEdit={canEdit} onEdit={() => setEditing(a)} onDelete={() => handleDelete(a)} onAprovar={() => handleAprovar(a)} aprovando={aprovarMut.isPending && aprovarMut.variables === a.id} />
          ))}
        </div>
      )}

      {/* Form modal — create or edit */}
      {showForm && <FormModal onClose={() => setShowForm(false)} />}
      {editing && <FormModal initial={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function AtivacaoCard({ ativacao: a, canEdit, onEdit, onDelete, onAprovar, aprovando }: { ativacao: AtivacaoMAC; canEdit: boolean; onEdit: () => void; onDelete: () => void; onAprovar: () => void; aprovando: boolean }) {
  const catStyle = CAT_COLORS[a.categoria] ?? 'bg-slate-50 border-slate-200 text-slate-600';

  return (
    <div className="bg-panel border border-border rounded-lg p-4 space-y-2 hover:shadow-sm transition-shadow group">
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-semibold text-sm text-text leading-tight">{a.nome}</h3>
        <div className="flex items-center gap-1 shrink-0">
          <span className={clsx(
            'text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-wide',
            a.status === 'ativa' ? 'bg-green-50 border-green-300 text-green-700' : 'bg-amber-50 border-amber-300 text-amber-700',
          )}>
            {a.status === 'ativa' ? 'ATIVA' : 'A CRIAR'}
          </span>
          {canEdit && (
            <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-1">
              <button type="button" onClick={onEdit} className="p-1 rounded hover:bg-slate-100 text-muted hover:text-blue" title="Editar">
                <Pencil className="w-3 h-3" />
              </button>
              <button type="button" onClick={onDelete} className="p-1 rounded hover:bg-red-50 text-muted hover:text-red-500" title="Excluir">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </div>

      <span className={clsx('inline-block text-[10px] font-medium px-2 py-0.5 rounded-full border', catStyle)}>
        {a.categoria}
      </span>

      {a.descricao && (
        <p className="text-xs text-muted line-clamp-2">{a.descricao}</p>
      )}

      <div className="flex items-center gap-3 pt-1">
        {a.landingPageUrl && (
          <a href={a.landingPageUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 text-[11px] text-blue hover:underline">
            <ExternalLink className="w-3 h-3" /> Landing page
          </a>
        )}
        {a.videoUrl && (
          <a href={a.videoUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 text-[11px] text-purple-600 hover:underline">
            <Video className="w-3 h-3" /> Vídeo
          </a>
        )}
      </div>

      {a.status !== 'ativa' && canEdit && (
        <button type="button" onClick={onAprovar} disabled={aprovando}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-green-600 text-white text-[11px] font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors">
          <Check className="w-3.5 h-3.5" /> {aprovando ? 'Aprovando…' : 'Aprovar ativação'}
        </button>
      )}
      {a.status === 'ativa' && a.aprovadaPor && (
        <p className="text-[10px] text-muted flex items-center gap-1">
          <Check className="w-3 h-3 text-green-600" />
          Aprovada por {a.aprovadaPor}{a.aprovadaEm ? ` em ${new Date(`${a.aprovadaEm}T12:00:00`).toLocaleDateString('pt-BR')}` : ''}
        </p>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'px-3 py-1.5 rounded-full text-[11px] font-medium border transition-colors',
        active
          ? 'bg-blue text-white border-blue'
          : 'bg-panel border-border text-muted hover:text-text hover:border-slate-300',
      )}
    >
      {children}
    </button>
  );
}

function FormModal({ onClose, initial }: { onClose: () => void; initial?: AtivacaoMAC }) {
  const qc = useQueryClient();
  const isEdit = !!initial;
  const [nome, setNome] = useState(initial?.nome ?? '');
  const [categoria, setCategoria] = useState<CategoriaAtivacao>(initial?.categoria ?? 'Real Estate');
  const [descricao, setDescricao] = useState(initial?.descricao ?? '');
  const [landingPageUrl, setLandingPageUrl] = useState(initial?.landingPageUrl ?? '');
  const [videoUrl, setVideoUrl] = useState(initial?.videoUrl ?? '');
  const [status, setStatus] = useState<StatusAtivacao>(initial?.status ?? 'ativa');

  const payload = { nome, categoria, descricao: descricao || undefined, landingPageUrl: landingPageUrl || undefined, videoUrl: videoUrl || undefined, status };

  const mut = useMutation({
    mutationFn: () => isEdit
      ? api.mac.ativacoes.update(initial!.id, payload)
      : api.mac.ativacoes.create(payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['mac-ativacoes'] }); onClose(); },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" tabIndex={-1} ref={(el)=>{if(el&&el.dataset.esc!=='1'){el.dataset.esc='1';el.focus();}}} onKeyDown={(e)=>{if(e.key==='Escape')onClose();}}>
      <div className="bg-panel rounded-xl border border-border shadow-xl w-full max-w-lg p-5 space-y-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-sm">{isEdit ? 'Editar Ativação' : 'Nova Ativação MAC'}</h2>
          <button type="button" onClick={onClose} className="text-muted hover:text-text"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <Field label="Nome *">
            <input value={nome} onChange={(e) => setNome(e.target.value)} className="input-field" placeholder="Ex: Fundo Imobiliário XYZ" />
          </Field>
          <Field label="Categoria">
            <select value={categoria} onChange={(e) => setCategoria(e.target.value as CategoriaAtivacao)} className="input-field">
              {CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select value={status} onChange={(e) => setStatus(e.target.value as StatusAtivacao)} className="input-field">
              <option value="ativa">Ativa</option>
              <option value="a_criar">A criar</option>
            </select>
          </Field>
          <Field label="Descrição">
            <textarea value={descricao} onChange={(e) => setDescricao(e.target.value)} rows={6} className="input-field resize-y" />
          </Field>
          <Field label="Landing page URL">
            <input value={landingPageUrl} onChange={(e) => setLandingPageUrl(e.target.value)} className="input-field" placeholder="https://..." />
          </Field>
          <Field label="Vídeo URL">
            <input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} className="input-field" placeholder="https://..." />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs rounded-md border border-border hover:bg-panel-2">Cancelar</button>
          <button
            type="button"
            disabled={!nome.trim() || mut.isPending}
            onClick={() => mut.mutate()}
            className="px-3 py-1.5 text-xs rounded-md bg-blue text-white font-medium hover:bg-blue/90 disabled:opacity-50"
          >
            {mut.isPending ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] font-medium text-muted mb-1 block">{label}</span>
      {children}
    </label>
  );
}
